/**
 * Date Utilities for UTC-5 (Eastern Time)
 * All dates sent to server should be in UTC-5 regardless of client location
 */

/**
 * Get current date/time in UTC-5
 * Returns a date string that represents UTC-5 time
 * @returns {string} Current date in UTC-5 as ISO string
 */
export function getCurrentDateUTC5() {
  const now = new Date();
  // Get UTC time
  const utcTime = now.getTime() + (now.getTimezoneOffset() * 60 * 1000);
  // Convert to UTC-5 (subtract 5 hours from UTC)
  const utc5OffsetMs = 5 * 60 * 60 * 1000; // 5 hours in milliseconds
  const utc5Time = new Date(utcTime - utc5OffsetMs);
  return utc5Time.toISOString();
}

/**
 * Convert a date to UTC-5 for server storage
 * @param {Date|string} date - Date to convert
 * @returns {string} Date string in UTC-5
 */
export function toUTC5ForServer(date) {
  if (!date) return null;
  
  let dateObj;
  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'string') {
    // If it's already a date string, parse it
    dateObj = new Date(date);
  } else {
    return null;
  }
  
  if (isNaN(dateObj.getTime())) return null;
  
  // Get UTC time
  const utcTime = dateObj.getTime() + (dateObj.getTimezoneOffset() * 60 * 1000);
  // Convert to UTC-5 (subtract 5 hours from UTC)
  const utc5OffsetMs = 5 * 60 * 60 * 1000;
  const utc5Time = new Date(utcTime - utc5OffsetMs);
  return utc5Time.toISOString();
}

/**
 * Convert a date string from server (UTC-5 stored as UTC) to UTC-5 Date object
 * @param {string|Date} serverDate - Date from server
 * @returns {Date} Date representing UTC-5 time
 */
export function fromServerToUTC5(serverDate) {
  if (!serverDate) return null;
  
  const dateObj = serverDate instanceof Date ? serverDate : new Date(serverDate);
  if (isNaN(dateObj.getTime())) return null;
  
  // Server stores UTC-5 as UTC, so we add 5 hours to get actual UTC-5 time
  const utc5OffsetMs = 5 * 60 * 60 * 1000;
  return new Date(dateObj.getTime() + utc5OffsetMs);
}

/**
 * Format date for display in UTC-5
 * @param {Date|string} date - Date to format
 * @param {string} format - Format type ('iso', 'date', 'datetime')
 * @returns {string} Formatted date string
 */
export function formatDateUTC5(date, format = 'iso') {
  if (!date) return null;
  
  const utc5Date = fromServerToUTC5(date);
  if (!utc5Date) return null;
  
  if (format === 'date') {
    // YYYY-MM-DD format
    const year = utc5Date.getUTCFullYear();
    const month = String(utc5Date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(utc5Date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  if (format === 'datetime') {
    // YYYY-MM-DDTHH:mm format
    const year = utc5Date.getUTCFullYear();
    const month = String(utc5Date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(utc5Date.getUTCDate()).padStart(2, '0');
    const hours = String(utc5Date.getUTCHours()).padStart(2, '0');
    const minutes = String(utc5Date.getUTCMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }
  
  // ISO format
  return utc5Date.toISOString();
}

/**
 * Convert a date string (from form input) to UTC-5 for server
 * Handles both date-only (YYYY-MM-DD) and datetime (YYYY-MM-DDTHH:mm) formats
 * @param {string} dateString - Date string from form
 * @returns {string} Date string in UTC-5 for server
 */
export function convertFormDateToUTC5(dateString) {
  if (!dateString || dateString.trim() === '') return '';
  
  try {
    // If it's already an ISO string, convert it
    if (dateString.includes('T') || dateString.includes('Z')) {
      return toUTC5ForServer(dateString);
    }
    
    // If it's just a date (YYYY-MM-DD), treat it as UTC-5 midnight
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const dateObj = new Date(dateString + 'T00:00:00-05:00');
      // Convert to UTC for server (subtract 5 hours)
      const utc5OffsetMs = 5 * 60 * 60 * 1000;
      const utcTime = new Date(dateObj.getTime() - utc5OffsetMs);
      return utcTime.toISOString();
    }
    
    // If it's datetime without timezone (YYYY-MM-DDTHH:mm), treat as UTC-5
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(dateString)) {
      const dateObj = new Date(dateString + '-05:00');
      // Convert to UTC for server (subtract 5 hours)
      const utc5OffsetMs = 5 * 60 * 60 * 1000;
      const utcTime = new Date(dateObj.getTime() - utc5OffsetMs);
      return utcTime.toISOString();
    }
    
    // Fallback: try to parse and convert
    return toUTC5ForServer(dateString);
  } catch (error) {
    console.warn('[dateUtils] Error converting date to UTC-5:', error);
    return dateString; // Return original if conversion fails
  }
}

/**
 * Create a date string in UTC-5 format from components
 * @param {number} year - Year
 * @param {number} month - Month (1-12)
 * @param {number} day - Day
 * @param {number} hour - Hour (0-23)
 * @param {number} minute - Minute (0-59)
 * @returns {string} Date string in UTC-5
 */
export function createDateUTC5(year, month, day, hour = 0, minute = 0) {
  const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00-05:00`;
  const dateObj = new Date(dateStr);
  // Convert to UTC for server (subtract 5 hours)
  const utc5OffsetMs = 5 * 60 * 60 * 1000;
  const utcTime = new Date(dateObj.getTime() - utc5OffsetMs);
  return utcTime.toISOString();
}

